import { AnimalsList } from "animals/AnimalsList";
import { LogActivityModal } from "animals/LogActivityModal";
import { AssignAnimalsToGroupVariables } from "api/MyLivestock/Groups/types/AssignAnimalsToGroup";
import { Button, Flex, Spacer, Title } from "components";
import { MoveGroupOfAnimalsToFieldButton } from "components/MyLivestock";
import { CommonContext } from "config/commonProvider";
import { TableOptionsContext } from "config/tableOptionsProvider";
import { ELEMENTS_IDS } from "constants/ElementsIds";
import { TABLE_IDS } from "constants/Interface";
import { AnimalFragment, useAssignAnimalsToGroupMutation, useGetGroupQuery } from "generated/graphql";
import { removeNothings } from "helpers/general";
import { useGetCurrentBusinessUnit } from "hooks";
import React, { useContext, useEffect, useState } from "react";
import styles from "./GroupAnimalsList.module.scss";

interface Props {
  groupId: string;
  addingAnimalsToGroup: boolean;
  setAddingAnimalsToGroup: (isAddingAnimals: boolean) => void;
}

export const GroupAnimalsList: React.FC<Props> = ({ groupId, addingAnimalsToGroup, setAddingAnimalsToGroup }) => {
  const { id: businessUnitId } = useGetCurrentBusinessUnit();
  const { data } = useGetGroupQuery({
    variables: { group: groupId, businessUnit: Number(businessUnitId) },
    fetchPolicy: "cache-only",
  });
  const [groupAnimalsIds, setGroupAnimalsIds] = useState<string[]>([]);
  const { showNotification } = useContext(CommonContext);
  const { getTableOptions } = useContext(TableOptionsContext);
  const tableOptions = getTableOptions<AnimalFragment>(TABLE_IDS["ANIMAL_LIST_SERVER"]);
  const selectedRowIds = tableOptions?.selectedRowsIds;
  const selectedAnimalIds = selectedRowIds ? [...selectedRowIds] : [];
  const [moveAnimalsToGroup] = useAssignAnimalsToGroupMutation();

  useEffect(() => {
    if (data?.group?.animals) {
      setGroupAnimalsIds(removeNothings(data.group.animals.map((animal) => animal?.id)));
    }
  }, [data]);

  const addAnimalToGroupHandler = async (selectedAnimalIds: Array<string>) => {
    setAddingAnimalsToGroup(false);

    try {
      const variables: AssignAnimalsToGroupVariables = {
        input: {
          group: groupId,
          animals: selectedAnimalIds,
          date: new Date(),
        },
      };
      const { data } = await moveAnimalsToGroup({
        variables,
      });

      if (data?.moveAnimalsToGroup?.success) {
        showNotification({
          variant: "success",
          message: "Group successfully updated.",
        });
      }
      if (data?.moveAnimalsToGroup?.errors && data?.moveAnimalsToGroup.errors.length > 0) {
        showNotification({
          variant: "error",
          message: data?.moveAnimalsToGroup.errors.map((error) => error?.message).join("; "),
        });
      }
    } catch (error: any) {
      showNotification({
        variant: "error",
        message: error?.message ? error.message : "Error moving animals to group. Please try again.",
      });
    }
  };

  return (
    <>
      <Flex container containerJustifyContent="space-between">
        <Spacer baselineHeight={3} />
        <Flex item itemGutter>
          <Title secondary>
            {addingAnimalsToGroup
              ? `Select livestock to add to your group (${selectedAnimalIds.length} selected)`
              : "Animals in this group"}
          </Title>
        </Flex>

        <Flex>
          {addingAnimalsToGroup ? (
            <>
              <Button
                id={ELEMENTS_IDS.GROUPS_EDIT_MODE_CANCEL_BUTTON}
                caption="Cancel"
                variant="hollow"
                colour="grey"
                className={styles.rightMargin}
                onClick={() => {
                  setAddingAnimalsToGroup(false);
                }}
              />
              <Button
                id={ELEMENTS_IDS.GROUPS_EDIT_MODE_ADD_TO_GROUP_BUTTON}
                disabled={selectedAnimalIds.length === 0}
                caption="Add to group"
                onClick={() => addAnimalToGroupHandler(selectedAnimalIds)}
              />
            </>
          ) : (
            <Flex container containerJustifyContent="space-between">
              <Flex container item itemGutter>
                <MoveGroupOfAnimalsToFieldButton />

                <Button
                  id={ELEMENTS_IDS.GROUP_ADD_ANIMALS_TO_GROUP_BUTTON}
                  caption="Add animals to group"
                  onClick={() => {
                    setAddingAnimalsToGroup(true);
                  }}
                />
              </Flex>
              {groupAnimalsIds.length > 0 ? (
                <Flex item>
                  <LogActivityModal animalIds={groupAnimalsIds} />
                </Flex>
              ) : null}
            </Flex>
          )}
        </Flex>
      </Flex>

      <Spacer baselineHeight={3} />

      <AnimalsList
        excludedFilters={{ groupId: true }}
        showAnimalActionButtons={false}
        showSelectColumn={addingAnimalsToGroup}
        groupId={addingAnimalsToGroup ? undefined : groupId}
        fromPage="group"
      />
    </>
  );
};
